import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PER_PAGE } from '@/lib/config';
import type { QueryParams } from '@/lib/api/client';

/**
 * List view state: pagination + filters + free text, **held in the URL**.
 *
 * It used to be `useState`, so a filtered view could not be bookmarked, reopened, or
 * sent to a colleague, because the address bar said the same thing whatever you were
 * looking at (PHASE 12 decision: list state lives in the URL).
 *
 * The public API is unchanged on purpose, so every list gains shareable state without
 * its call site changing.
 *
 * ## What the URL looks like
 *
 * Filters are written under their own names — `?status=new&assigned_to=<id>` —
 * rather than packed into an opaque blob. A support thread can read one, and it
 * keeps the deep-links the dashboard already emits working natively instead of
 * by seeding.
 *
 * `page` is omitted at 1 and `per_page` at the default, so an untouched list
 * has a clean address; `q` is omitted when empty.
 *
 * ## Booleans
 *
 * A filter value is a string or a boolean (`missing_ar` on the content list),
 * and the URL only carries text. `'true'`/`'false'` decode back to booleans;
 * anything else stays a string. A filter whose legitimate *string* value is
 * literally "true" or "false" must therefore be declared in `initialFilters`,
 * where the declared type wins over the literal decode. No filter in the
 * console is in that position today — they are statuses, ids, dates and slugs —
 * and the escape hatch exists so that stops being a landmine if one ever is.
 *
 * ## History
 *
 * Every write **replaces** the entry rather than pushing one. Typing into a
 * filter should not build a back-stack you have to walk out of; Back leaves the
 * list, which is what `FilterBar`'s `useUrlFilters` already does. The two hooks
 * share the page-reset rule for the same reason: page 4 of a different query is
 * not a place.
 *
 * ## Lists that are not the page
 *
 * A list rendered inside a modal — the media picker, opened from whatever screen
 * you happened to be on — calls {@link useLocalListState} instead. Its state is
 * not the address's to describe: paging a picker would rewrite the *host* list's
 * `page` and `q` underneath it, and closing the picker would leave its `kind=`
 * behind in a URL that no longer has a picker in it. Same API, same behaviour,
 * component-local storage. The rule is about ownership, not about modality: if
 * the list is the reason you are at this address, it belongs in the URL; if it
 * is a transient tool you opened on top of one, it does not.
 *
 * Two hooks rather than one hook with a `{ url: false }` flag, because the flag
 * could not honestly be honoured: a hook cannot skip `useSearchParams()` on some
 * renders, so the opt-out still required a Router overhead and still threw
 * without one. Split, the modal list has no router dependency to
 * mis-mount.
 */
export interface ListState {
  page: number;
  perPage: number;
  filters: Record<string, string | boolean | undefined>;
  search: string;
  params: QueryParams;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
  setFilter: (key: string, value: string | boolean | undefined) => void;
  setSearch: (q: string) => void;
  reset: () => void;
  /** True when anything is narrowing the list — drives the "Clear filters" affordance. */
  active: boolean;
}

/** Params this hook owns outright; everything else in the URL is left alone. */
const RESERVED = ['page', 'per_page', 'q'] as const;

/**
 * Params that say what is OPEN over the list (`?id=` — a record's dialog, deep-linked from a
 * notification email), not what narrows it. They are never sent as a filter, never count as
 * "filtered", and survive "Clear filters", which must not close the record being read.
 */
const UI_PARAMS = ['id'] as const;

function decode(raw: string, declared: string | boolean | undefined): string | boolean {
  if (typeof declared === 'string') return raw;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return raw;
}

/** How a store hands its params back to be rewritten. */
type ParamsWriter = (apply: (previous: URLSearchParams) => URLSearchParams) => void;

/**
 * List state held in the address bar. The default, and what every routed list
 * in the console uses.
 */
export function useListState(
  initialFilters: Record<string, string | boolean | undefined> = {},
): ListState {
  const [urlParams, setUrlParams] = useSearchParams();
  // Replace rather than push: typing into a filter must not build a back-stack.
  const write = useCallback<ParamsWriter>(
    (apply) => setUrlParams(apply, { replace: true }),
    [setUrlParams],
  );
  return useListStateStore(initialFilters, urlParams, write);
}

/**
 * The same list state, held in the component instead of the URL, for a list that
 * does not own the address it is rendered over (see "Lists that are not the
 * page"). Uses no router hooks, so it is safe anywhere in the tree.
 */
export function useLocalListState(
  initialFilters: Record<string, string | boolean | undefined> = {},
): ListState {
  const [localParams, setLocalParams] = useState(() => new URLSearchParams());
  return useListStateStore(initialFilters, localParams, setLocalParams);
}

/** Everything both stores share: decoding, derived params, and the setters. */
function useListStateStore(
  initialFilters: Record<string, string | boolean | undefined>,
  searchParams: URLSearchParams,
  writeParams: ParamsWriter,
): ListState {

  /*
   * `initialFilters` is nearly always an object literal, so it is a new
   * reference on every render and would re-run every memo below. It declares
   * which keys this list owns and what type each is — it is a shape, not a
   * value — so it is captured once and compared by content thereafter.
   */
  const declaredRef = useRef(initialFilters);
  const declaredKey = JSON.stringify(initialFilters);
  const lastKey = useRef(declaredKey);
  if (lastKey.current !== declaredKey) {
    lastKey.current = declaredKey;
    declaredRef.current = initialFilters;
  }
  const declared = declaredRef.current;

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const perPage = Number(searchParams.get('per_page')) || DEFAULT_PER_PAGE;
  const search = searchParams.get('q') ?? '';

  const filters = useMemo(() => {
    const next: Record<string, string | boolean | undefined> = { ...declared };
    for (const [key, raw] of searchParams.entries()) {
      if ((RESERVED as readonly string[]).includes(key)) continue;
      if ((UI_PARAMS as readonly string[]).includes(key)) continue;
      next[key] = decode(raw, declared[key]);
    }
    return next;
  }, [searchParams, declared]);

  const write = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      writeParams((current) => {
        const next = new URLSearchParams(current);
        mutate(next);
        return next;
      });
    },
    [writeParams],
  );

  const setPage = useCallback(
    (p: number) =>
      write((params) => {
        const clamped = Math.max(1, p);
        if (clamped > 1) params.set('page', String(clamped));
        else params.delete('page');
      }),
    [write],
  );

  const setPerPage = useCallback(
    (n: number) =>
      write((params) => {
        if (n === DEFAULT_PER_PAGE) params.delete('per_page');
        else params.set('per_page', String(n));
        params.delete('page');
      }),
    [write],
  );

  const setFilter = useCallback(
    (key: string, value: string | boolean | undefined) =>
      write((params) => {
        // `false` and `''` are "not filtering by this", the same as `undefined`:
        // that is the rule `params` below has always applied, and a URL carrying
        // `missing_ar=false` would describe a filter that is not doing anything.
        if (value === undefined || value === '' || value === false) params.delete(key);
        else params.set(key, String(value));
        params.delete('page');
      }),
    [write],
  );

  const setSearch = useCallback(
    (q: string) =>
      write((params) => {
        if (q.trim()) params.set('q', q);
        else params.delete('q');
        params.delete('page');
      }),
    [write],
  );

  const reset = useCallback(
    () =>
      write((params) => {
        for (const key of [...params.keys()]) {
          if (key === 'per_page') continue; // a density choice, not a filter
          if ((UI_PARAMS as readonly string[]).includes(key)) continue;
          params.delete(key);
        }
      }),
    [write],
  );

  const params = useMemo<QueryParams>(() => {
    const p: QueryParams = { page, per_page: perPage };
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== '' && v !== false) p[k] = v as string | boolean;
    }
    if (search.trim()) p.q = search.trim();
    return p;
  }, [page, perPage, filters, search]);

  const active = useMemo(
    () =>
      search.trim().length > 0 ||
      Object.values(filters).some((v) => v !== undefined && v !== '' && v !== false),
    [filters, search],
  );

  return {
    page,
    perPage,
    filters,
    search,
    params,
    setPage,
    setPerPage,
    setFilter,
    setSearch,
    reset,
    active,
  };
}
